<template>
    <div class="return-page">
        <div class="page-content">
            <div class="b2b-shipping-billing-summary">
                <shipping-confirm v-model="shipping" :allowEdit="false" d2c/>
                <billing-confirm v-model="billing" :allowEdit="false" d2c/>
            </div>
            <div class="items-available-to-return" id="tableContainer2">
                <div class="title-container">
                    <div class="title-l">Items available to Return</div>
                    <div class="content text-rothcoText">
                        <div class="paragraph-default">{{ totalItems }} Items</div>
                    </div>
                </div>
                <return-items-table v-model="formItems"  :availableReturnReasons="returnReasons" :checkable="true" :errors="returnForm.errors" />
            </div>

            <return-amount-summary :returnAmount="returnCost" /> 

            <div class="return-footer">
                <div class="input-row">
                    <div class="field-state order-comments">
                        <div class="textarea-folder">
                            <PrimeTextArea 
                                id="return_comments"
                                name="return_comments"
                                placeholder=" " class="input-folder--input"
                                v-model="returnForm.comments"
                                >
                            </PrimeTextArea>
                            <label class="paragraph-default" for="return_comments">Additional Comments (Optional)</label>
                        </div>
                    </div>
                    <div class="input-details">
                        <p class="paragraph-details">Return Details: You can return most new, unopened items within 30 days of delivery for a full refund. Credit for the returned item(s) will be applied to the credit method that was used to make the purchase. Returns will be applied to the purchasing credit card within 10 to 15 days from the date we received the merchandise. Only purchases made directly from Rothco.com qualify for the return. Please visit our Return Policy for more information</p>
                    </div>
                    <div v-if="firstError" class="notification notification-error">
                        <p class="paragraph-details"><strong>Error Notification</strong> {{firstError}}</p>
                    </div>
                </div>
                <RothcoCta ref="submitReturnBtn" type="button" @click="submit" class="primary return-btn" aria-label="Continue to return" :disabled="returnForm.processing">
                    Complete Return
                </RothcoCta>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, toRefs, watch, ref} from "vue";
    import { Link, usePage, router } from "@inertiajs/vue3";
    import { string, array, object, number, boolean} from 'yup';
    import { useForm } from "@/helpers/composables/useForm";
    import { route } from "ziggy-js";
    import ShippingConfirm from "@/components/ShippingConfirm.vue";
    import BillingConfirm from "@/components/BillingConfirm.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import ReturnAmountSummary from "@/components/ReturnAmountSummary.vue";
    import ReturnItemsTable from "@/components/ReturnItemsTable.vue";
    import { useSettingsStore } from "@/store/settings";
    import { useReturnOrder } from "@/helpers/composables/useReturnOrder";
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages"
    import {
        productUploadImageResponsiveFromFilename,
        PRODUCT_IMAGE_SIZES_RETURN_TABLE
    } from "@/helpers/productUploadImageUrl";
    
    let { getReturnCost } = useReturnOrder({ router });
    let { setOverlayMessage } = useSettingsStore();
    const props = defineProps({
        order: {
            type: Object,
            default() {
                return {
                    billing_name: "",
                    billing_address: "",
                    billing_address_2: "",
                    billing_city: "",
                    billing_state: "",
                    billing_country: "",
                    billing_zip: "",
                    billing_phone: "",
                    billing_email: "",
                    billing_comments: "",
                    /**
                     * make shipping fields
                     */
                    shipping_name: "",
                    shipping_address: "",
                    shipping_address_2: "",
                    shipping_city: "",
                    shipping_state: "",
                    shipping_country: "",
                    shipping_zip: "",
                    shipping_phone: "",
                    shipping_email: "",
                    shipping_comments: "",

                    order_items: []
                };
            }
        },
        returnReasons: {
            type: Object,
            default: {}
        }
    });
    let { order , returnReasons} = toRefs(props);

    let items = computed(() => {
        return [...order.value.order_items.map((item) => {
                return {
                    ...item,
                    price: item.price,
                    quantity: item.quantity,
                    return_quantity: null,
                    return_reason: null,
                    selected_to_return: false,
                    variation: item.item_variation,
                    item_name: item.item_variation.item?.name,
                    item_no: item.item_variation.rothcoItem?.rothco_item_no,
                    variants_text: Object.entries(item?.item_variation?.text_description ?? {})
                                .map(([key, value]) => `${key}: ${value}`)
                                .join(' - '),
                    variants_badges: item.item_variation.text_description,
                    seo_name: item.item_variation.item?.seo_name,
                    ...(() => {
                        const fn = item.item_variation.primary_image?.image_filename;
                        if (!fn) {
                            return {
                                image: comingSoonImages().small,
                                image_srcset: undefined,
                                image_sizes: undefined
                            };
                        }
                        const pack = productUploadImageResponsiveFromFilename(
                            fn,
                            PRODUCT_IMAGE_SIZES_RETURN_TABLE
                        );
                        return {
                            image: pack.src,
                            image_srcset: pack.srcSet,
                            image_sizes: pack.sizes
                        };
                    })()
                };
            })
        ];
    });

    let billing = computed(() => {
        return {
            billing_name: order.value.billing_name,
            billing_address: order.value.billing_address,
            billing_address_2: order.value.billing_address_2,
            billing_city: order.value.billing_city,
            billing_state: order.value.billing_state,
            billing_country: order.value.billing_country,
            billing_zip: order.value.billing_zip,
            billing_phone: order.value.billing_phone,
            billing_credit_card_id: order.value.billing_credit_card_id,
            billing_po_number: order.value.po_number,
            billing_payment_method: order.value.billing_payment_method,
            order_type: order.value.order_type,
            billing_email: order.value.billing_email,
            billing_alternate_email: order.value.billing_alternate_email,
            billing_comments: order.value.billing_comments,
            creditCard: order.value.creditCard,
        };
    });

    let shipping = computed(() => {
        return {
            shipping_name: order.value.shipping_name,
            shipping_in_care_of: order.value.shipping_in_care_of,
            shipping_must_ship_date: order.value.shipping_must_ship_date,
            shipping_method: order.value.shipping_method,
            shipping_address: order.value.shipping_address,
            shipping_address_2: order.value.shipping_address_2,
            shipping_city: order.value.shipping_city,
            shipping_state: order.value.shipping_state,
            shipping_country: order.value.shipping_country,
            shipping_zip: order.value.shipping_zip,
            shipping_phone: order.value.shipping_phone,
            shipping_email: order.value.shipping_email,
            shipping_final_destination_state:
                order.value.shipping_final_destination_state,
            shipping_final_destination_country:
                order.value.shipping_final_destination_country,
            freight_payment_term: order.value.freight_payment_term,
            collect_no_id: order.value.collect_no_id,
            shipping_comments: order.value.shipping_comments,
        };
    });

    const returnForm = useForm({
        useInertia: true,
        inertiaAlias: '',
        schema: object({
            order: object().default({}),
            items: array().of(
                    object({
                        return_quantity: number().when('selected_to_return', {
                            is: (val)=>(val == true),
                            then: (schema)=>schema.required('Return quantity is required'),
                            otherwise: (schema)=>schema.notRequired()
                        }).default(null),

                        return_reason: number().when('selected_to_return', {
                            is: (val)=>(val == true),
                            then: (schema)=>schema.required('Return reason is required'),
                            otherwise: (schema)=>schema.notRequired()
                        }).default(null)
                    })
                ).required().default(items.value),
            comments: string().default(''),
        })
     });

    const formItems = ref(returnForm.items);

    watch(formItems, (val) => {
        returnForm.items = val;
    }, { deep: true });


    const watchedAttributes = computed(() =>
        returnForm.items.map(item => ({
            return_quantity: item.return_quantity
        }))
    );

    const firstError = computed(() => {    
        return Object.values(errors.value)[0] || null
    });


    watch(watchedAttributes, () => {
        clearErrors();
        getCalculatedReturnCost();
    }, { deep: true });

    const returnCost = ref({
        subtotal: 0,
        couponAdjust: 0,
        tax: 0,
        amount: 0
    });

    const returnItems = computed(() => {
        return returnForm.items.filter((item) => item.return_quantity > 0 && item.selected_to_return)
                .map((item) => ({
                    order_item_id: item.order_item_id,
                    quantity: item.return_quantity,
                    reason: item.return_reason,
                    itemVariation: item.variation.item_variation_id,
        }));
    });

    const isFormValid = computed(() => {
        return returnItems.value.length > 0  && returnItems.value.every(item => item.reason > 0)
    });

    const getCalculatedReturnCost = () => {
        getReturnCost(
            {
                items: returnItems.value,
                order: order.value.order_id
            }, {
            afterFetch: (data) => {
                returnCost.value = data.data;
            }
        });
    }


    let errors = ref({});

    const clearErrors = () => {
        errors.value = {};
    }

    const submit = () => {
        if(returnForm.processing) return;
        
        if(!isFormValid.value || returnForm.hasErrors) {            
            errors.value.return =  'Please select quantity and return reason to continue';
            return;
        }

        returnForm
                .transform((data) => {  
                    data.items = returnItems.value;
                    data.order = order.value.order_id;         
                    return data;
                })
                .post(
                route("return.submit", order.value.order_id),
                {
                    onStart: () => {
                        setOverlayMessage('Submitting your return. This process can take 10 - 15 seconds. Please do not refresh the page.');
                    },
                    onError: () => {
                        setOverlayMessage(null);
                        errors.value = usePage().props.errors;
                    },
                    onSuccess: () => {
                        setOverlayMessage(null);
                        // router.visit(route("return.success", order.value.order_id));
                    }
                }
        );
    }


    const totalItems = computed(()=> {
        return items.value?.length || 0; 
    });

    let submitReturnBtn = ref(null);
</script>
